import Vue from 'vue';
import _ from 'lodash';

import { readHeartbeat } from '@/services/heartbeat';

export const state = {
  heartbeats: {},
  mapFilters: [],
  recentlyUsedMapFilters: [],
  lastKnownHeartbeat: null,
};

export const FILTER_TYPES = Object.freeze({
  PLANIFICATION_GROUP: 'planificationGroup',
});

export const mutations = {
  setHeartbeats(state, heartbeats = []) {
    // Each heartbeat is always replaced wholesale (never mutated property by property), so
    // it doesn't need Vue's deep reactivity conversion - freezing it skips that per-property
    // Object.defineProperty walk, which otherwise runs on every single incoming message.
    heartbeats.forEach(heartbeat => {
      Vue.set(state.heartbeats, heartbeat.username, Object.freeze(heartbeat));
    });
  },

  setHeartbeat(state, heartbeat) {
    Vue.set(state.heartbeats, heartbeat.username, Object.freeze(heartbeat));
  },

  removeHeartbeat(state, heartbeat) {
    Vue.delete(state.heartbeats, heartbeat.username);
  },

  setMapFilters(state, mapFilters) {
    state.mapFilters = mapFilters;
  },

  addMapFilter(state, mapFilter) {
    state.mapFilters.push(mapFilter);
  },

  addRecentlyUsedMapFilter(state, mapFilter) {
    if (state.recentlyUsedMapFilters.length === 3) {
      state.recentlyUsedMapFilters.pop();
    }

    state.recentlyUsedMapFilters.unshift(mapFilter);
  },

  setLastKnownHeartbeat(state, heartbeat) {
    state.lastKnownHeartbeat = heartbeat;
  },
};

export const actions = {
  setMapFilters({ state, commit, dispatch }, mapFilters) {
    let difference = mapFilters.filter(
      filter => !state.mapFilters.includes(filter)
    );

    if (difference.length) {
      dispatch('addRecentlyUsedMapFilterIfNotExists', difference[0]);
    }

    commit('setMapFilters', mapFilters);
  },

  addMapFilterIfNotExists({ getters, commit }, mapFilter) {
    if (!getters.filterExistsFor('mapFilters', mapFilter)) {
      commit('addMapFilter', mapFilter);
    }
  },

  addRecentlyUsedMapFilterIfNotExists({ getters, commit }, mapFilter) {
    if (!getters.filterExistsFor('recentlyUsedMapFilters', mapFilter)) {
      commit('addRecentlyUsedMapFilter', mapFilter);
    }
  },

  clearMapFilters({ commit }) {
    commit('setMapFilters', []);
  },

  readHeartbeat({ commit }, username) {
    return readHeartbeat(username).then(response => {
      commit('setLastKnownHeartbeat', response.data);
      return response.data;
    });
  },
};

export const getters = {
  filterExistsFor: state => (mapFilters, mapFilter) => {
    return state[mapFilters].some(
      localFilter =>
        localFilter.path === mapFilter.path &&
        localFilter.value === mapFilter.value
    );
  },

  userHasFilters: (state, getters) => user => {
    return (
      getters.userHasUserFilters(user) ||
      getters.userHasPlanificationFilters(user)
    );
  },

  userHasUserFilters: state => user => {
    return state.mapFilters.some(
      ({ path, value }) => _.get(user, path) === value
    );
  },

  userHasPlanificationFilters: state => user => {
    return state.mapFilters
      .filter(filter => filter.type === FILTER_TYPES.PLANIFICATION_GROUP)
      .some(({ path, value }) =>
        value.flat_houston_users.includes(_.get(user, path))
      );
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
